import type { PDFDocumentProxy } from 'pdfjs-dist'
import type { TextItem } from 'pdfjs-dist/types/src/display/api'
import { supabase } from './supabase'
import { base } from './db'
import type { Feuillet, LienPage, Zone } from './types'

/**
 * Liens entre les pages d'un plan : la bulle « 3 / A402 » d'une élévation
 * mène à la page A402, centrée sur le détail 3.
 *
 * Les liens sont trouvés dans le texte du PDF (plans exportés d'AutoCAD ou
 * de Revit), enregistrés sur le serveur, puis gardés sur l'appareil pour
 * fonctionner sans réseau. Un administrateur peut supprimer un lien erroné
 * ou en ajouter un à la main ; une nouvelle détection ne touche pas à ces
 * corrections.
 */

const COLONNES = 'id, document_id, page, zone, cible_page, cible_point, libelle, source, statut'

// ---------------------------------------------------------------------
// Lecture (serveur, puis copie sur l'appareil)
// ---------------------------------------------------------------------

export async function liensLocaux(documentId: string): Promise<LienPage[]> {
  const tous = await base.liens.where('document_id').equals(documentId).toArray()
  return tous.filter((l) => l.statut === 'actif')
}

/** Rafraîchit la copie locale à partir du serveur ; sans réseau, garde la copie. */
export async function rafraichirLiens(documentId: string): Promise<LienPage[]> {
  if (navigator.onLine) {
    const { data, error } = await supabase.from('liens_pages').select(COLONNES).eq('document_id', documentId)
    if (!error && data) {
      await base.transaction('rw', base.liens, async () => {
        await base.liens.where('document_id').equals(documentId).delete()
        await base.liens.bulkPut(data as LienPage[])
      })
    }
  }
  return liensLocaux(documentId)
}

// ---------------------------------------------------------------------
// Corrections à la main (administrateur, réseau requis)
// ---------------------------------------------------------------------

function horsLigne(): never {
  throw new Error('Pas de réseau : les liens se modifient seulement en ligne.')
}

export async function creerLien(l: Omit<LienPage, 'id' | 'source' | 'statut'>): Promise<LienPage> {
  if (!navigator.onLine) horsLigne()
  const { data, error } = await supabase
    .from('liens_pages')
    .insert({ ...l, source: 'manuel', statut: 'actif' })
    .select(COLONNES)
    .single()
  if (error) throw error
  await base.liens.put(data as LienPage)
  return data as LienPage
}

/** Change la page visée. Le lien devient « manuel » : la détection n'y touche plus. */
export async function modifierCible(l: LienPage, cible: number, point: LienPage['cible_point']): Promise<LienPage> {
  if (!navigator.onLine) horsLigne()
  const changement = {
    cible_page: cible,
    cible_point: point,
    source: 'manuel' as const,
    modifie_le: new Date().toISOString()
  }
  const { error } = await supabase.from('liens_pages').update(changement).eq('id', l.id)
  if (error) throw error
  const n: LienPage = { ...l, cible_page: cible, cible_point: point, source: 'manuel' }
  await base.liens.put(n)
  return n
}

/**
 * Supprime un lien. Un lien trouvé automatiquement est seulement marqué
 * « rejeté », pour que la prochaine détection ne le propose pas de nouveau.
 */
export async function supprimerLien(l: LienPage): Promise<void> {
  if (!navigator.onLine) horsLigne()
  if (l.source === 'auto') {
    const { error } = await supabase
      .from('liens_pages')
      .update({ statut: 'rejete', modifie_le: new Date().toISOString() })
      .eq('id', l.id)
    if (error) throw error
  } else {
    const { error } = await supabase.from('liens_pages').delete().eq('id', l.id)
    if (error) throw error
  }
  await base.liens.delete(l.id)
}

// ---------------------------------------------------------------------
// Détection automatique
// ---------------------------------------------------------------------
//
// Une bulle de détail, c'est un numéro de détail (« 3 ») juste au-dessus
// d'un numéro de feuillet (« A402 »), dans un cercle. On cherche donc, sur
// chaque page, les numéros de feuillet connus de l'index ; le numéro de
// détail est le petit texte le plus proche, de taille semblable.
//
// Sur la page visée, le titre du détail porte la même bulle (« 3 / A402 »)
// avec le nom du détail à sa droite : c'est là que le lien mène.

interface Jeton {
  texte: string
  norm: string // majuscules, sans espaces ni ponctuation : « A-402 » → « A402 »
  x0: number
  y0: number
  x1: number
  y1: number
  cx: number
  cy: number
  h: number // hauteur des caractères
}

interface Renvoi {
  cible: number
  code: string
  num: string | null
  zone: { x0: number; y0: number; x1: number; y1: number }
  cx: number
  cy: number
  h: number
}

interface AnalysePage {
  W: number
  H: number
  jetons: Jeton[]
  renvois: Renvoi[]
}

export type NouveauLien = Omit<LienPage, 'id'>

const normaliser = (s: string) =>
  s
    .toUpperCase()
    .normalize('NFD')
    .replace(/[^A-Z0-9]/g, '')

/** Numéro de détail : 3, 12, 3A, B, B2… (pas « A200B », qui est un feuillet) */
const RE_NUM = /^(?:\d{1,3}[A-Z]?|[A-Z]\d{0,2}[A-Z]?)$/

async function jetonsPage(pdf: PDFDocumentProxy, n: number) {
  const page = await pdf.getPage(n)
  const vue = page.getViewport({ scale: 1 })
  const contenu = await page.getTextContent()
  const jetons: Jeton[] = []
  for (const i of contenu.items as TextItem[]) {
    if (typeof i.str !== 'string' || !i.str.trim()) continue
    // « A 4 0 2 » (lettres espacées) → « A402 »
    const s = /^(\S )+\S$/.test(i.str.trim()) ? i.str.trim().replace(/ /g, '') : i.str
    const [a, b, c, d, e, f] = i.transform as number[]
    const na = Math.hypot(a, b) || 1
    const nc = Math.hypot(c, d) || 1
    const ux = a / na
    const uy = b / na
    const vx = c / nc
    const vy = d / nc
    const taille = nc
    const largeur = Math.abs(i.width) || taille * 0.6 * s.length
    // Chaque mot (séparé par une espace ou « / ») devient un jeton, placé
    // proportionnellement dans la largeur du texte.
    for (const m of s.matchAll(/[^\s/]+/g)) {
      const debut = (largeur * (m.index ?? 0)) / s.length
      const fin = (largeur * ((m.index ?? 0) + m[0].length)) / s.length
      const coins = [
        [e + ux * debut - vx * taille * 0.2, f + uy * debut - vy * taille * 0.2],
        [e + ux * fin - vx * taille * 0.2, f + uy * fin - vy * taille * 0.2],
        [e + ux * debut + vx * taille, f + uy * debut + vy * taille],
        [e + ux * fin + vx * taille, f + uy * fin + vy * taille]
      ].map(([x, y]) => vue.convertToViewportPoint(x, y))
      const xs = coins.map((p) => p[0])
      const ys = coins.map((p) => p[1])
      const x0 = Math.min(...xs)
      const x1 = Math.max(...xs)
      const y0 = Math.min(...ys)
      const y1 = Math.max(...ys)
      jetons.push({
        texte: m[0],
        norm: normaliser(m[0]),
        x0,
        y0,
        x1,
        y1,
        cx: (x0 + x1) / 2,
        cy: (y0 + y1) / 2,
        h: taille
      })
    }
  }
  page.cleanup()
  return { W: vue.width, H: vue.height, jetons }
}

/** Renvois d'une page : numéros de feuillet connus, avec leur numéro de détail. */
function trouverRenvois(jetons: Jeton[], codes: Map<string, { page: number; code: string }>): Renvoi[] {
  const renvois: Renvoi[] = []
  for (const j of jetons) {
    const c = codes.get(j.norm)
    if (!c) continue
    let num: Jeton | null = null
    let dMin = j.h * 2.4
    for (const k of jetons) {
      if (k === j || !RE_NUM.test(k.norm) || codes.has(k.norm)) continue
      const rapport = k.h / j.h
      if (rapport < 0.6 || rapport > 1.7) continue
      const d = Math.hypot(k.cx - j.cx, k.cy - j.cy)
      if (d < dMin) {
        dMin = d
        num = k
      }
    }
    const marge = j.h * 0.45
    const x0 = Math.min(j.x0, num?.x0 ?? j.x0) - marge
    const y0 = Math.min(j.y0, num?.y0 ?? j.y0) - marge
    const x1 = Math.max(j.x1, num?.x1 ?? j.x1) + marge
    const y1 = Math.max(j.y1, num?.y1 ?? j.y1) + marge
    renvois.push({
      cible: c.page,
      code: c.code,
      num: num?.norm ?? null,
      zone: { x0, y0, x1, y1 },
      cx: (x0 + x1) / 2,
      cy: (y0 + y1) / 2,
      h: j.h
    })
  }
  return renvois
}

/** Vrai si un texte (le nom du détail) suit la bulle sur sa droite. */
function aUnTitre(r: Renvoi, jetons: Jeton[]): boolean {
  return jetons.some(
    (j) =>
      /[A-ZÀ-Ü]{3}/i.test(j.texte) &&
      j.x0 > r.zone.x1 - r.h &&
      j.x0 < r.zone.x1 + r.h * 8 &&
      Math.abs(j.cy - r.cy) < r.h * 2.5 &&
      normaliser(j.texte) !== normaliser(r.code)
  )
}

function chevauche(a: Zone, b: Zone): boolean {
  const l = Math.min(a.x + a.l, b.x + b.l) - Math.max(a.x, b.x)
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y)
  if (l <= 0 || h <= 0) return false
  return (l * h) / Math.min(a.l * a.h, b.l * b.h) > 0.3
}

/**
 * Lit toutes les pages et retourne les liens trouvés. Il faut que l'index
 * des pages (codes des feuillets) soit déjà fait : un renvoi vers une
 * feuille qui n'est pas dans le document est ignoré.
 */
export async function detecterLiens(
  pdf: PDFDocumentProxy,
  documentId: string,
  feuillets: Feuillet[],
  progression?: (fait: number, total: number) => void,
  arret?: () => boolean
): Promise<NouveauLien[]> {
  const codes = new Map<string, { page: number; code: string }>()
  for (const f of [...feuillets].sort((a, b) => a.page - b.page)) {
    if (!f.code) continue
    const n = normaliser(f.code)
    if (n.replace(/\D/g, '').length >= 2 && !codes.has(n)) codes.set(n, { page: f.page, code: f.code })
  }
  if (codes.size === 0) return []

  const total = pdf.numPages
  const analyses: AnalysePage[] = []
  for (let n = 1; n <= total; n++) {
    if (arret?.()) return []
    try {
      const { W, H, jetons } = await jetonsPage(pdf, n)
      analyses.push({ W, H, jetons, renvois: trouverRenvois(jetons, codes) })
    } catch {
      analyses.push({ W: 1, H: 1, jetons: [], renvois: [] })
    }
    progression?.(n, total)
  }

  // Titres des détails : sur chaque page, les bulles qui portent son propre
  // numéro de feuillet, suivies du nom du détail.
  const titres = new Map<number, Map<string, Renvoi>>()
  analyses.forEach((a, i) => {
    const page = i + 1
    const parNum = new Map<string, Renvoi>()
    const propres = a.renvois.filter((r) => r.cible === page)
    // Le numéro de la cartouche est écrit beaucoup plus gros que les bulles :
    // ce n'est pas un titre de détail, même avec un chiffre à côté.
    const hBulle = Math.min(...propres.map((r) => r.h))
    for (const r of propres) {
      if (!r.num || r.h > hBulle * 1.4) continue
      const deja = parNum.get(r.num)
      if (!deja || (!aUnTitre(deja, a.jetons) && aUnTitre(r, a.jetons))) parNum.set(r.num, r)
    }
    titres.set(page, parNum)
  })

  const liens: NouveauLien[] = []
  analyses.forEach((a, i) => {
    const page = i + 1
    const dePage: NouveauLien[] = []
    for (const r of a.renvois) {
      const titre = r.num ? titres.get(r.cible)?.get(r.num) : undefined
      if (r.cible === page) {
        // Renvoi vers un autre détail de la même feuille. La bulle du titre
        // elle-même (et le numéro de la cartouche) ne sont pas des liens.
        if (!titre || titre === r || Math.hypot(titre.cx - r.cx, titre.cy - r.cy) < r.h * 3) continue
      }
      const ca = analyses[r.cible - 1]
      const zone: Zone = {
        x: r.zone.x0 / a.W,
        y: r.zone.y0 / a.H,
        l: (r.zone.x1 - r.zone.x0) / a.W,
        h: (r.zone.y1 - r.zone.y0) / a.H
      }
      const lien: NouveauLien = {
        document_id: documentId,
        page,
        zone,
        cible_page: r.cible,
        cible_point: titre && ca ? { x: titre.cx / ca.W, y: titre.cy / ca.H } : null,
        libelle: r.num ? `${r.num}/${r.code}` : r.code,
        source: 'auto',
        statut: 'actif'
      }
      // Même bulle lue deux fois (texte en double dans le PDF) : un seul lien.
      if (dePage.some((l) => l.cible_page === lien.cible_page && chevauche(l.zone, lien.zone))) continue
      dePage.push(lien)
    }
    liens.push(...dePage)
  })
  return liens
}

/**
 * Remplace les liens automatiques du document par ceux de la détection.
 * Les liens ajoutés ou corrigés à la main restent, et un lien automatique
 * déjà rejeté n'est pas proposé de nouveau.
 */
export async function enregistrerDetection(documentId: string, nouveaux: NouveauLien[]): Promise<number> {
  const { data, error } = await supabase
    .from('liens_pages')
    .select(COLONNES)
    .eq('document_id', documentId)
  if (error) throw error
  const existants = (data as LienPage[]) ?? []
  const manuels = existants.filter((l) => l.source === 'manuel' && l.statut === 'actif')
  const rejetes = existants.filter((l) => l.statut === 'rejete')
  const garder = nouveaux.filter(
    (n) =>
      !manuels.some((m) => m.page === n.page && chevauche(m.zone, n.zone)) &&
      !rejetes.some((r) => r.page === n.page && r.cible_page === n.cible_page && chevauche(r.zone, n.zone))
  )

  const { error: e1 } = await supabase
    .from('liens_pages')
    .delete()
    .eq('document_id', documentId)
    .eq('source', 'auto')
    .eq('statut', 'actif')
  if (e1) throw e1
  for (let i = 0; i < garder.length; i += 500) {
    const { error: e2 } = await supabase.from('liens_pages').insert(garder.slice(i, i + 500))
    if (e2) throw e2
  }
  await supabase
    .from('documents')
    .update({ liens_detectes_le: new Date().toISOString() })
    .eq('id', documentId)
  await rafraichirLiens(documentId)
  return garder.length + manuels.length
}
